import { useState, useContext } from "react";
import { CartContext } from "../contexts";
import { getCartStockState } from "../utils/cartStock";
import { useDialogA11y } from "./dialogA11y";

export const CopyBtn = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      style={{
        fontFamily: "var(--font-mono)", fontSize: "9px",
        color: copied ? "var(--green)" : "var(--text-muted)",
        padding: "3px 8px", border: "1px solid var(--border)", borderRadius: 3,
        letterSpacing: "0.06em", flexShrink: 0,
        transition: "all 0.2s",
        background: copied ? "rgba(74,158,106,0.1)" : "transparent",
        transform: copied ? "scale(1.05)" : "scale(1)",
      }}
    >{copied ? "✓ COPIED" : "COPY"}</button>
  );
};

export const Badge = ({ status }) => {
  const colors = {
    delivered: "#4a9e6a", complete: "#4a9e6a", shipped: "#e8a020", processing: "#5588cc",
    cancelled: "#d94040", failed: "#d94040", "pending payment": "#9b6fd4", confirmed: "#4a9e6a",
    scheduled: "#e8a020", sending: "#5588cc",
  };
  const c = colors[status] || "#8a8880";
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: "3px",
      background: c + "18", color: c, border: `1px solid ${c}33`,
      fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.05em", textTransform: "uppercase",
    }}>{status}</span>
  );
};

export const Btn = ({ children, onClick, variant = "primary", small, style: s, disabled, type = "button" }) => {
  const [pressed, setPressed] = useState(false);
  const base = {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px",
    padding: small ? "8px 16px" : "12px 24px",
    fontSize: small ? "13px" : "14px",
    fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
    borderRadius: "4px",
    transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1, outline: "none",
    transform: pressed && !disabled ? "scale(0.97)" : "scale(1)",
  };
  const variants = {
    primary: {
      background: "var(--amber)", color: "#111", border: "2px solid var(--amber)",
      boxShadow: pressed ? "none" : "0 2px 8px rgba(0,212,170,0.25)",
    },
    ghost: {
      background: "transparent", color: "var(--text)", border: "2px solid var(--border)",
      boxShadow: "none",
    },
    danger: {
      background: "transparent", color: "var(--red)", border: "2px solid var(--red)",
      boxShadow: "none",
    },
  };
  return (
    <button type={type}
      onClick={disabled ? undefined : (e) => { e.currentTarget.blur(); onClick?.(e); }}
      onMouseDown={() => !disabled && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{ ...base, ...variants[variant], ...s }}
    >{children}</button>
  );
};

export const NavBtn = ({ isActive, onClick, children }) => {
  const [hovered, setHovered] = useState(false);
  const style = {
    display: "inline-flex", alignItems: "center", gap: "6px",
    padding: "7px 14px", fontSize: "13px", fontWeight: 700,
    letterSpacing: "0.04em", textTransform: "uppercase",
    borderRadius: "4px", transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
    cursor: "pointer", outline: "none",
    background: isActive ? "rgba(0,212,170,0.08)" : hovered ? "rgba(255,255,255,0.04)" : "transparent",
    color: isActive ? "var(--amber)" : hovered ? "var(--text)" : "var(--text-dim)",
    border: isActive ? "2px solid var(--amber-dim)" : "2px solid transparent",
  };
  return (
    <button type="button" style={style} onClick={(e) => { e.currentTarget.blur(); onClick?.(e); }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {children}
    </button>
  );
};

export const AddToCartBtn = ({ product }) => {
  const { cart, dispatch } = useContext(CartContext);
  const [show, setShow] = useState(false);
  const [adding, setAdding] = useState(false);

  const { soldOut, atStockLimit } = getCartStockState(product, cart);

  const handleClick = () => {
    if (soldOut || atStockLimit) return;
    dispatch({ type: "ADD", product });
    setAdding(true);
    setShow(false);
    requestAnimationFrame(() => setShow(true));
    setTimeout(() => { setShow(false); setAdding(false); }, 1600);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
      {show && (
        <span
          key={Date.now()}
          style={{
            fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--green)", fontWeight: 700,
            whiteSpace: "nowrap", animation: "cartConfirmFade 1.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
          }}
        >✓ Added</span>
      )}
      {soldOut
        ? <span style={{
            fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--red)", fontWeight: 700,
            padding: "6px 12px", background: "rgba(217,64,64,0.1)", borderRadius: 3, border: "1px solid rgba(217,64,64,0.2)",
          }}>SOLD OUT</span>
        : atStockLimit
          ? <span style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)" }}>Max qty</span>
          : <Btn small onClick={handleClick} style={adding ? { background: "var(--green)", borderColor: "var(--green)", animation: "cartBounce 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)" } : {}}>
              {adding ? "✓ Added" : "+ Cart"}
            </Btn>
      }
    </div>
  );
};

export const Modal = ({ open, onClose, title, children }) => {
  const { ref, onKeyDown, titleId, dialogProps } = useDialogA11y({ open, onClose });
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
        zIndex: 1000, animation: "fadeIn 0.2s ease both",
      }} />
      <div onClick={onClose} style={{
        position: "fixed", inset: 0,
        zIndex: 1001, overflowY: "auto", WebkitOverflowScrolling: "touch", willChange: "transform",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "60px 16px 24px",
      }}>
        <div ref={ref} onClick={e => e.stopPropagation()} onKeyDown={onKeyDown} {...dialogProps} style={{
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: "6px", padding: 28, maxWidth: 480, width: "100%",
          marginBottom: 24, flexShrink: 0,
          boxShadow: "var(--shadow-lg)",
          animation: "fadeIn 0.2s ease both",
        }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 id={titleId} style={{ fontSize: "20px" }}>{title}</h3>
          <button onClick={onClose} style={{
            fontSize: "20px", color: "var(--text-muted)", width: 32, height: 32,
            display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: "50%", transition: "all 0.15s",
          }}
            onMouseEnter={e => { e.target.style.background = "var(--surface2)"; e.target.style.color = "var(--text)"; }}
            onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.color = "var(--text-muted)"; }}
          >×</button>
        </div>
        {children}
        </div>
      </div>
    </>
  );
};

// Favorites heart: outline when not saved, filled when saved. One definition for the shop
// card, the Favorites chip and the product page.
export const HeartIcon = ({ filled = false }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-9.2-9.3C1.6 7.8 3.9 4.5 7.3 4.5c2 0 3.5 1.1 4.7 2.7 1.2-1.6 2.7-2.7 4.7-2.7 3.4 0 5.7 3.3 4.5 6.7-1.7 4.7-9.2 9.3-9.2 9.3z" /></svg>
);
